import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
  SpecialFieldKeys,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { empty, eq, inList, not } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';
import { LANGUAGES_SHORTLIST } from '../standards/options.js';

export const QUESTIONS: { [key: string]: ApplicationQuestion } = {
  // Section 1: Applicant Information
  [CommonQuestionKeys.RACE]: STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.RACE],
  [CommonQuestionKeys.GENDER]: STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.GENDER],
  sexualIdentity: {
    key: 'sexualIdentity',
    copy: { title: 'Which of the following do you consider yourself to be?' },
    skippable: true,
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Sexual Identity',
        key: 'sexualIdentity.sexualIdentity',
        props: { multiple: true },
        validation: { required: false },
        options: [
          { label: 'Bisexual', value: 'bisexual' },
          { label: 'Gay', value: 'gay' },
          { label: 'Heterosexual/ Straight', value: 'heterosexual' },
          { label: 'Lesbian', value: 'lesbian' },
          { label: 'Queer', value: 'queer' },
          { label: 'Questioning', value: 'questioning' },
          { label: 'I use a different term (specify)', value: 'other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'sexualIdentity.otherSexualIdentity',
        dynamicLogic: inList('sexualIdentity.sexualIdentity', 'other'),
        copy: 'Specify',
      },
    ],
  },
  preferredLanguageReading: {
    key: 'preferredLanguageReading',
    copy: { title: 'What language do you prefer to read?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'preferredLanguageReading',
        copy: 'Preferred language for reading',
        options: LANGUAGES_SHORTLIST,
      },
    ],
  },
  preferredLanguageSpeaking: {
    key: 'preferredLanguageSpeaking',
    copy: { title: 'What language do you prefer to speak?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'preferredLanguageSpeaking',
        copy: 'Preferred language for speaking',
        options: LANGUAGES_SHORTLIST,
      },
    ],
  },
  [CommonQuestionKeys.PHONE_NUMBER]: {
    key: CommonQuestionKeys.PHONE_NUMBER,
    copy: { title: 'What is your phone number?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Phone number',
        key: CommonQuestionKeys.PHONE_NUMBER,
        inputType: TextInputType.Phone,
      },
    ],
  },
  [CommonQuestionKeys.MAILING_ADDRESS]: {
    key: CommonQuestionKeys.MAILING_ADDRESS,
    copy: { title: 'What is your mailing address?' },
    fields: [
      {
        type: FieldType.Address,
        copy: 'Address',
        key: CommonQuestionKeys.MAILING_ADDRESS,
        filter: { state: 'CA' },
      },
    ],
  },
  // Section 2: Household information
  householdRepresentative: {
    key: 'householdRepresentative',
    copy: { title: 'Are you the representative of the household?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'householdRepresentative',
        options: YesNoOptions,
      },
    ],
  },
  householdRepresentativeInfo: {
    key: 'householdRepresentativeInfo',
    copy: { title: 'What is the household representative’s first and last name?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'householdRepresentativeFirstName',
        copy: 'First Name',
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'householdRepresentativeLastName',
        copy: 'Last Name',
      },
    ],
  },
  householdMembers: {
    key: 'householdMembers',
    copy: { title: 'Who else is in your household?' },
    fields: [
      {
        type: FieldType.Complex,
        key: 'householdMembers',
        copy: 'Household members',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'First Name',
            key: 'firstName',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Last Name',
            key: 'LastName',
          },
          {
            type: FieldType.Date,
            copy: 'Date of Birth',
            key: CommonQuestionKeys.DATE_OF_BIRTH,
            validation: { required: true, allowedRange: 'past' },
            props: { helperText: 'Example: 06/01/2004' },
          },
          {
            type: FieldType.Dropdown,
            key: 'householdRelationship',
            props: { multiple: true },
            copy: 'What is your relationship with this person?',
            options: [
              { label: 'Head of Household', value: 'head-of-household' },
              { label: 'Dependent', value: 'dependent' },
              { label: 'Spouse', value: 'spouse' },
              { label: 'Domestic Partner', value: 'domestic-partner' },
            ],
          },
        ],
      },
    ],
  },
  publicAssistance: {
    key: 'publicAssistance',
    copy: { title: 'Has anyone in your household ever received public assistance?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'publicAssistance',
        options: YesNoOptions,
      },
    ],
  },
  enrolledPrograms: {
    key: 'enrolledPrograms',
    copy: { title: 'What program was it?' },
    dynamicLogic: eq('publicAssistance', 'yes'),
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'enrolledPrograms',
        props: { multiple: true },
        copy: 'Enrolled Programs',
        options: [
          {
            label: 'Temporary Assistance for Needy Families',
            value: 'temporaryAssistanceForNeedyFamilies',
          },
          { label: 'Tribal TANF', value: 'tribalTANF' },
          { label: 'Medicaid', value: 'medicaid' },
          {
            label: 'Supplemental Nutrition Assistance Program',
            value: 'supplementalNutritionAssistanceProgram',
          },
          {
            label: 'General Assistance/General Relief',
            value: 'generalAssistance/generalRelief',
          },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'otherEnrolledPrograms',
        dynamicLogic: inList('enrolledPrograms', 'other'),
        copy: 'Other',
      },
    ],
  },
  pregnancy: {
    key: 'pregnancy',
    copy: { title: 'Is anyone in your household pregnant or a teen parent?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'pregnancy',
        options: YesNoOptions,
      },
    ],
  },
  fosterChild: {
    key: 'fosterChild',
    copy: {
      title:
        'Is there a foster child currently living in your home who is receiving foster care services?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'fosterChild',
        options: YesNoOptions,
      },
    ],
  },
  // Section 3: Employment & Income information
  employed: {
    key: 'employed',
    copy: { title: 'Are you currently employed?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'employed',
        options: YesNoOptions,
      },
    ],
  },
  job: {
    key: 'job',
    copy: { title: 'Please tell us a little more about your current job(s)' },
    dynamicLogic: eq('employed', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: 'job',
        copy: 'Job',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Job Title',
            key: CommonQuestionKeys.JOB_TITLE,
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Employer Name',
            key: 'employerName',
          },
          {
            type: FieldType.Dropdown,
            key: 'typeEmployment',
            copy: 'Type of employment is this?',
            options: [
              { label: 'Part-time', value: 'part-time' },
              { label: 'Full-time', value: 'full-time' },
              { label: 'Contract', value: 'contract' },
              { label: 'Temporary', value: 'temporary' },
              { label: 'Seasonal', value: 'seasonal' },
            ],
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Employer Address',
            key: CommonQuestionKeys.EMPLOYER_ADDRESS,
          },
          {
            type: FieldType.Text,
            copy: 'Employer phone number',
            key: 'employerPhoneNumber',
            inputType: TextInputType.Phone,
          },
        ],
      },
    ],
  },
  unEarnedIncome: {
    key: 'unEarnedIncome',
    copy: {
      title:
        'Do you or anyone in your household get income that does not come from a job (unearned)?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'unEarnedIncome',
        options: YesNoOptions,
      },
    ],
  },
  externalIncome: {
    key: 'externalIncome',
    copy: { title: 'Please check all types of unearned income that apply from these examples.' },
    dynamicLogic: eq('unEarnedIncome', 'yes'),
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'externalIncome',
        props: { multiple: true },
        copy: 'External Income',
        options: [
          {
            label: 'Social Security',
            value: 'social-security',
          },
          { label: 'SSI/SSP', value: 'ssi-ssp' },
          { label: 'Medicaid', value: 'medicaid' },
          {
            label: 'Cash aid',
            value: 'cash-aid',
          },
          {
            label: 'CalWORKs/TANF/GA/GR/CAPI',
            value: 'calworks-tanf-ga-gr-capi',
          },
          {
            label: 'Room and board (from your renter)',
            value: 'room-and-board',
          },
          { label: 'Pension', value: 'pension' },
          { label: 'Child/Spousal support', value: 'child-spousal-support,' },
          {
            label: 'Government/railroad disability or retirement',
            value: 'government-railroad-disability-or-retirement',
          },
          {
            label: 'Veteran benefits or Military pension',
            value: 'veteran-benefits',
          },
          {
            label: 'Financial aid',
            value: 'financial-aid',
            children: [
              { label: 'School Grants', value: 'school-grants' },
              { label: 'Loans', value: 'loans' },
              { label: 'Scholarships', value: 'scholarships' },
            ],
          },
          { label: 'Gift of money', value: 'gift-of-money' },
          {
            label: 'Unemployment Insurance/State Disability Insurance (SDI)',
            value: 'unemployment-insurance-state-disability-insurance',
          },
          { label: 'Worker’s compensation', value: 'sorkers-compensation' },
          {
            label: 'Lottery/gambling winnings',
            value: 'lottery-gambling-winnings',
          },
          {
            label: 'Help with rent/food/clothing',
            value: 'help-with-rent-food-clothing',
          },
          {
            label: 'Insurance or legal settlements',
            value: 'insurance-or-legal-settlements',
          },
          {
            label: 'Private disability or retirement',
            value: 'private-disability-or-retirement',
          },
          { label: 'Strike benefits', value: 'strike-benefits' },
          { label: 'Other', value: 'other' },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'otherExternalIncome',
        dynamicLogic: inList('externalIncome', 'other'),
        copy: 'Other',
      },
    ],
  },
  incomeExpiration: {
    key: 'incomeExpiration',
    copy: { title: 'If this income is not expected to continue, please explain:' },
    dynamicLogic: not(empty('externalIncome')),
    fields: [
      {
        type: FieldType.Complex,
        key: 'incomeExpiration',
        copy: 'Income',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Person getting the money',
            key: 'personGettingTheMoney',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'From where?',
            key: 'fromWhere',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'How much?',
            key: 'howMuch',
          },
          {
            type: FieldType.Dropdown,
            key: 'howOften',
            copy: 'How often received?',
            options: [
              { label: 'Once', value: 'once' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Other', value: 'other' },
            ],
          },
          {
            type: FieldType.Dropdown,
            key: 'expectToContinue',
            copy: 'Expect to continue?',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'incomeNotContinueReason',
            copy: 'please explain:',
            dynamicLogic: eq('expectToContinue', 'no'),
            props: { multiline: true, rows: 5 },
            validation: { required: false },
          },
        ],
      },
    ],
  },
  lostJob: {
    key: 'lostJob',
    copy: {
      title:
        'Has anyone lost a job, changed jobs, quit a job, or reduced work hours within the last 60 days?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'lostJob',
        options: YesNoOptions,
      },
    ],
  },
  // Section 4: Your Expenses
  expenses: {
    key: 'expenses',
    copy: { title: 'Does anyone not in your household help you pay for the expenses listed?' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'expenseslist',
        copy: `
- Food
- Domestic Care
- Medical
- Tuition
- Transportation
- Rent or house payment
- Property taxes and insurance (if billed separate from rent or mortgage)
- Gas, electric, or other fuel used for heating or cooling, such as firewood or propane (if separate from rent or mortgage)
- Telephone/cell phone
- Homeless Shelter Expense
- Water, sewage, garbage`,
        props: { variant: 'largeBody', alignment: 'left' },
      },
      {
        type: FieldType.RadioList,
        key: 'expenses',
        options: YesNoOptions,
      },
    ],
  },
  spendOnMeals: {
    key: 'spendOnMeals',
    copy: { title: 'How much does your household typically spend on meals in a given month?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Meal Expenses',
        key: 'spendOnMeals',
      },
    ],
  },
  spendOnUtilitiesAndHousing: {
    key: 'spendOnUtilitiesAndHousing',
    copy: {
      title: 'How much does your household spend on utilities and housing in a given month?',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Utilities and Housing Expenses',
        key: 'spendOnUtilitiesAndHousing',
      },
    ],
  },
  financialAssistanceRequest: {
    key: SpecialFieldKeys.RequestedAmount,
    copy: {
      title: 'Financial Assistance Request',
      intro:
        'Please enter the total amount (up to {MAX_AMOUNT}) of emergency cash assistance you believe will cover the total amount of expenses you have.',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Requested Amount',
        key: SpecialFieldKeys.RequestedAmount,
        props: { helperText: 'Max limit = {MAX_AMOUNT}' },
      },
    ],
  },
  comments: {
    key: 'comments',
    copy: { title: 'Is there anything else you’d like to share with us?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'comments.commentsField',
        copy: 'Comments (optional)',
        props: { multiline: true, rows: 5 },
        validation: { required: false },
      },
    ],
  },
  // Section 5: Your Supporting Documents
  supportingDocuments: {
    key: 'supportingDocuments',
    copy: { title: 'Supporting Documents' },
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.supportingDocuments',
        copy: {
          title: 'Supporting Documents',
          description:
            'Examples include benefit determination letter(s) (WIC, SNAP, etc.), recent paystubs, utility bills, car repairs or other documentation that may support your case.',
        },
      },
    ],
  },
  // Section 6: Application Review
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: {
      title: 'Before completing the application, please confirm this statement:',
      intro: 'Please review and confirm the statement by selecting the checkbox.',
    },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: "I, {APPLICANT_NAME}, certify under penalty of law, based on information and belief formed after reasonable inquiry, the statements and information contained in these documents are true, accurate and complete. I acknowledge that my information can be used by {PARTNER_NAME}'s staff to refer me to other programs.",
      },
    ],
  },
};

const COMMON_SECTIONS: ApplicationSection[] = [
  {
    key: 'applicantInformation',
    name: 'Applicant Information',
    overview: {
      title: 'Applicant Information',
      description:
        'In this section, we will ask some basic questions about you like address, phone number and language preference.',
    },
    questionGroups: [
      {
        key: 'applicantinformation',
        name: 'Applicant Information',
        questions: [
          QUESTIONS.race,
          QUESTIONS.gender,
          QUESTIONS.sexualIdentity,
          QUESTIONS.preferredLanguageReading,
          QUESTIONS.preferredLanguageSpeaking,
          QUESTIONS.phoneNumber,
          QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS],
        ],
      },
    ],
  },
  {
    key: 'householdInformation',
    name: 'Household Information',
    overview: {
      title: 'Household Information',
      description:
        'In this section, we will ask information about the people you live and share meals with.',
    },
    questionGroups: [
      {
        key: 'householdInformation',
        name: 'Household Information',
        questions: [
          QUESTIONS.householdRepresentative,
          QUESTIONS.householdRepresentativeInfo,
          QUESTIONS.householdMembers,
          QUESTIONS.publicAssistance,
          QUESTIONS.enrolledPrograms,
          QUESTIONS.pregnancy,
          QUESTIONS.fosterChild,
        ],
      },
    ],
  },
  {
    key: 'employment&IncomeInformation',
    name: 'Employment & Income information',
    overview: {
      title: 'Employment & Income information',
      description:
        'In this section, we will ask for information about your current employment & additional sources of income.',
    },
    questionGroups: [
      {
        key: 'employment&IncomeInformation',
        name: 'Employment & Income information',
        questions: [
          QUESTIONS.employed,
          QUESTIONS.job,
          QUESTIONS.unEarnedIncome,
          QUESTIONS.externalIncome,
          QUESTIONS.incomeExpiration,
          QUESTIONS.lostJob,
        ],
      },
    ],
  },
  {
    key: 'expenses',
    name: 'Your Expenses',
    overview: {
      title: 'Your Expenses',
      description: 'In this section, we’ll ask for information about your current expenses.',
    },
    questionGroups: [
      {
        key: 'expenses',
        name: 'Your Expenses',
        questions: [
          QUESTIONS.expenses,
          QUESTIONS.spendOnMeals,
          QUESTIONS.spendOnUtilitiesAndHousing,
          QUESTIONS.financialAssistanceRequest,
          QUESTIONS.comments,
        ],
      },
    ],
  },
  {
    key: 'supportingDocuments',
    name: 'Your supporting documents',
    overview: {
      title: 'Your supporting documents',
      description:
        'Your documents will be carefully reviewed. Upload any documents you have available that you believe will help your eligibility.',
    },
    questionGroups: [
      {
        key: 'supportingDocuments',
        name: 'Your supporting documents',
        questions: [QUESTIONS.supportingDocuments],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections.review,
      {
        overview: {
          description: `
Almost done. Let’s make sure everything looks correct before submitting.

In order to make sure your application can be reviewed as soon as possible, you’ll now
have a chance to review all the answers you provided to make sure everything looks
correct.`,
        },
        // Remove the Application Assistance question
        questionGroups: [null],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is West County Child Care Subsidies?',
      body: `
West County  child care subsidies support all families in accessing high quality child 
care, the Child Care Assistance Program (CCAP) provides financial assistance to low-income 
families while they are working or attending school. To learn more, go to [{FAQ_PAGE}]({FAQ_PAGE}).`,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  sections: COMMON_SECTIONS,
};
export default CONFIG;
